import moment from 'moment';
import { printYesOrNo } from '@cogitate/ui-multi-utils';

const OVER_54_UW_FIELD_KEYS = [
  'IsUsingPrescriptionMedicationsRegularly',
  'PrescriptionMedicationUnderlyingAilmentNote',
  'HasRoutineAnnualPhysicalExaminations',
  'IsUnderRegularPhysicianCareForUnderlyingAilment',
  'HasAdvisedDiagnosticTestingOrEvaluationNotCompleted',
  'IsAwaitingMedicalConsultationsTestsOrProcedures',
  'HasAdvisedSurgeryOrTreatmentNotOccurred',
  'HasUpcomingTravelPhysicallyEnduringExcursions',
  'RegularlyParticipatesInRecreationalSports',
];

const DOB_PARSE_FORMATS = [moment.ISO_8601, 'YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY', 'MM-DD-YYYY'];

export function parsePayrollProtectionDateOfBirth(dateOfBirth) {
  if (dateOfBirth == null || dateOfBirth === '') {
    return null;
  }
  if (typeof dateOfBirth === 'object' && dateOfBirth instanceof Date) {
    const fromDate = moment(dateOfBirth);
    return fromDate.isValid() ? fromDate.startOf('day') : null;
  }
  if (typeof dateOfBirth === 'object' && (dateOfBirth.year != null || dateOfBirth.month != null)) {
    const y = dateOfBirth.year ?? dateOfBirth.Year;
    const m = (dateOfBirth.month ?? dateOfBirth.Month ?? 1) - 1;
    const d = dateOfBirth.day ?? dateOfBirth.Day ?? 1;
    const fromParts = moment({ year: y, month: m, date: d });
    return fromParts.isValid() ? fromParts.startOf('day') : null;
  }
  let birthDate = moment(dateOfBirth, DOB_PARSE_FORMATS, true);
  if (!birthDate.isValid()) {
    birthDate = moment(dateOfBirth);
  }
  if (!birthDate.isValid()) {
    return null;
  }
  return birthDate.startOf('day');
}

export function getPayrollProtectionPractitionerDob(practitioner, policy) {
  if (practitioner && (practitioner.DateOfBirth || practitioner.PractitionerDateOfBirth)) {
    return practitioner.DateOfBirth || practitioner.PractitionerDateOfBirth;
  }
  return policy?.InsuredAccount?.OtherDetails?.DateOfBirth ?? null;
}

export function calculateAgeFromDateOfBirth(dateOfBirth) {
  const birthDate = parsePayrollProtectionDateOfBirth(dateOfBirth);
  if (!birthDate) {
    return NaN;
  }
  const currentDate = moment();
  let age = currentDate.diff(birthDate, 'years');
  if (
    currentDate.month() < birthDate.month() ||
    (currentDate.month() === birthDate.month() && currentDate.date() < birthDate.date())
  ) {
    age -= 1;
  }
  return age;
}

export function isPractitionerAge55OrOlderByStoredAge(age) {
  return !Number.isNaN(age) && age > 54;
}

export function isPractitionerMoreThan54YearsPlusOneDay(dateOfBirth) {
  const age = calculateAgeFromDateOfBirth(dateOfBirth);
  return !Number.isNaN(age) && age > 54;
}

export function isPractitionerAge65OrOlderByDate(dateOfBirth) {
  const age = calculateAgeFromDateOfBirth(dateOfBirth);
  return !Number.isNaN(age) && age >= 65;
}

export function isPractitionerMoreThan54YearsPlusOneDayByStoredAge(age) {
  return isPractitionerAge55OrOlderByStoredAge(age);
}

export function isPractitionerAge65OrOlderByStoredAge(age) {
  return !Number.isNaN(age) && age >= 65;
}

export function isPractitionerIn55To65AnswerReferralBandByDate(dateOfBirth) {
  const age = calculateAgeFromDateOfBirth(dateOfBirth);
  return !Number.isNaN(age) && age > 54 && age < 66;
}

export function isPractitionerIn55To65AnswerReferralBandByStoredAge(age) {
  return !Number.isNaN(age) && age > 54 && age < 66;
}

export function practitionerTriggersOver54UwQuestions(dateOfBirth, storedAge) {
  if (dateOfBirth != null && dateOfBirth !== '') {
    const age = calculateAgeFromDateOfBirth(dateOfBirth);
    if (!Number.isNaN(age)) {
      return age > 54;
    }
  }
  return isPractitionerAge55OrOlderByStoredAge(storedAge);
}

export function resolvePayrollProtectionPractitionerAges(policy) {
  const unit = policy?.Risks?.PayrollProtectionUnit?.[0];
  if (!unit) {
    return { primaryAge: NaN, additionalAges: [] };
  }

  const primaryDob = policy?.InsuredAccount?.OtherDetails?.DateOfBirth;
  let primaryAge = primaryDob ? calculateAgeFromDateOfBirth(primaryDob) : Number(unit?.PractitionerRiskAttributes?.Age);

  const additionalAges = (unit?.AdditionalPractitioners || []).map((p) => {
    const dob = p?.DateOfBirth || p?.PractitionerDateOfBirth;
    if (dob) {
      return calculateAgeFromDateOfBirth(dob);
    }
    return Number(p?.Age);
  });

  return { primaryAge, additionalAges };
}

export function practitionerTriggersAge65Referral(dateOfBirth, storedAge) {
  if (dateOfBirth != null && dateOfBirth !== '') {
    const age = calculateAgeFromDateOfBirth(dateOfBirth);
    if (!Number.isNaN(age)) {
      return age >= 65;
    }
  }
  return isPractitionerAge65OrOlderByStoredAge(storedAge);
}

export function isAnyPayrollProtectionPractitionerAge65OrOlder(policy) {
  const unit = policy?.Risks?.PayrollProtectionUnit?.[0];
  if (!unit) {
    return false;
  }

  if (
    practitionerTriggersAge65Referral(
      policy?.InsuredAccount?.OtherDetails?.DateOfBirth,
      Number(unit?.PractitionerRiskAttributes?.Age)
    )
  ) {
    return true;
  }

  return (unit?.AdditionalPractitioners || []).some((p) =>
    practitionerTriggersAge65Referral(p?.DateOfBirth || p?.PractitionerDateOfBirth, Number(p?.Age))
  );
}

export function isPayrollProtectionOver54UwBand(policy) {
  const unit = policy?.Risks?.PayrollProtectionUnit?.[0];
  if (!unit) {
    return false;
  }

  const primaryDob = policy?.InsuredAccount?.OtherDetails?.DateOfBirth;
  if (practitionerTriggersOver54UwQuestions(primaryDob, Number(unit?.PractitionerRiskAttributes?.Age))) {
    return true;
  }

  return (unit?.AdditionalPractitioners || []).some((p) =>
    practitionerTriggersOver54UwQuestions(p?.DateOfBirth || p?.PractitionerDateOfBirth, Number(p?.Age))
  );
}

export function applyResolvedPayrollProtectionAgesToPolicy(policy) {
  const unit = policy?.Risks?.PayrollProtectionUnit?.[0];
  if (!unit?.PractitionerRiskAttributes) {
    return policy;
  }
  const { primaryAge, additionalAges } = resolvePayrollProtectionPractitionerAges(policy);
  if (!Number.isNaN(primaryAge)) {
    unit.PractitionerRiskAttributes.Age = String(primaryAge);
  }
  (unit.AdditionalPractitioners || []).forEach((practitioner, index) => {
    const age = additionalAges[index];
    if (!Number.isNaN(age)) {
      practitioner.Age = String(age);
    }
  });
  return policy;
}

const PAYROLL_PROTECTION_UI_ONLY_POLICY_FIELDS = [
  'ShowOver54UwQuestions',
  'AdditionalPractitionerOver54Count',
  'PrimaryPractitionerAge',
  'CurrentUserRole',
];

export function stripPayrollProtectionRiskQualifiersUiFields(model) {
  if (!model) {
    return model;
  }
  PAYROLL_PROTECTION_UI_ONLY_POLICY_FIELDS.forEach((field) => {
    delete model[field];
  });
  return model;
}

function clonePolicyForPayrollProtection(policy) {
  try {
    return structuredClone(policy);
  } catch {
    try {
      return JSON.parse(JSON.stringify(policy));
    } catch {
      return policy;
    }
  }
}

export function preparePayrollProtectionPolicyForRiskQualifiers(policy) {
  if (!policy || policy?.Attributes?.Lob !== 'PP') {
    return { policy, showOver54UwQuestions: false };
  }
  try {
    const clone = clonePolicyForPayrollProtection(policy);
    applyResolvedPayrollProtectionAgesToPolicy(clone);
    const showOver54UwQuestions = isPayrollProtectionOver54UwBand(clone);
    clone.ShowOver54UwQuestions = showOver54UwQuestions;
    if (!clone.Underwriting) {
      clone.Underwriting = { ...(policy.Underwriting || {}) };
    }
    return {
      policy: clearPayrollProtectionOver54UnderwritingIfNotInBand(clone),
      showOver54UwQuestions,
    };
  } catch {
    const showOver54UwQuestions = isPayrollProtectionOver54UwBand(policy);
    return { policy, showOver54UwQuestions };
  }
}

export function buildPayrollProtectionRiskQualifiersFormModel(policy, extraFields = {}) {
  const { policy: basePolicy, showOver54UwQuestions } = preparePayrollProtectionPolicyForRiskQualifiers(policy);

  return {
    ...basePolicy,
    ShowOver54UwQuestions: !!showOver54UwQuestions,
    ...extraFields,
  };
}

export function hasPayrollProtectionOver54UwAnswers(underwriting) {
  if (!underwriting) {
    return false;
  }
  return OVER_54_UW_FIELD_KEYS.some((key) => {
    const value = underwriting[key];
    if (value === '' || value == null || value === undefined) {
      return false;
    }
    return true;
  });
}

export function shouldShowPayrollProtectionOver54UwOnSummary(policy) {
  return isPayrollProtectionOver54UwBand(policy);
}

export function clearPayrollProtectionOver54UnderwritingIfNotInBand(policy) {
  if (!policy?.Underwriting || isPayrollProtectionOver54UwBand(policy)) {
    return policy;
  }
  const clone = structuredClone(policy);
  OVER_54_UW_FIELD_KEYS.forEach((key) => {
    clone.Underwriting[key] = '';
  });
  return clone;
}

export function payrollProtectionUnderwritingToggleAnswer(value) {
  if (value === '' || value == null || value === undefined) {
    return '--';
  }
  return printYesOrNo(value);
}

export function isPayrollProtectionUnderwritingYes(value) {
  return value === 'true' || value === true;
}

// Backward-compatible aliases
export const isPractitionerAgeOver54 = isPractitionerAge55OrOlderByStoredAge;
export const isPractitionerAge65OrOlder = isPractitionerAge65OrOlderByStoredAge;
export const isPractitionerAgeIn55To65ReferralBand = isPractitionerIn55To65AnswerReferralBandByStoredAge;
